import {
  formatPremiumAmount,
  premiumCurrency,
  type TiPremiumPrice,
} from '../../calculatedAttributes/providers/tiQuote';
import type { MaterialIconName } from '../../types/materialIconName';
import { formatData } from '../../utils/helpers/formatDate';
import type { JsonObject } from '../../utils/json';
import { tr } from '../../utils/translations/tr';
import type { PlanTierSummaryTrip } from './types';

// The resolved CONTENT_TRANSLATIONS bag accepted by `tr`.
export type Content = Parameters<typeof tr>[1];

// A quote without its own symbol prints the brand currency, as every other TI
// price cell does; dollar currencies (HK$, S$, US$, …) then collapse to "$".
const symbolOf = (
  price: TiPremiumPrice,
  brandCurrency: string | undefined,
): string => {
  const raw = premiumCurrency(price, brandCurrency);
  return raw?.includes('$') ? '$' : (raw ?? '');
};

// A tier's offered price as the TI provider page prints it, e.g. "$1,184.00".
export const formatOfferedPrice = (
  price: TiPremiumPrice,
  brandCurrency: string | undefined,
): string =>
  `${symbolOf(price, brandCurrency)}${formatPremiumAmount(price.amount)}`;

export type TierPrice = {
  offered: string;
  original?: string;
  discountLabel?: string;
  period: string;
};

// Tier-card price line, e.g. "$24" + struck "$40" + "50% off" + "SGD / trip".
export const formatTierPrice = (
  price: TiPremiumPrice,
  isAnnual: boolean,
  currencyCode: string,
  content: Content,
  brandCurrency: string | undefined,
): TierPrice => {
  const { originalAmount, discountPercent } = price;
  return {
    offered: formatOfferedPrice(price, brandCurrency),
    original:
      originalAmount !== undefined
        ? `${symbolOf(price, brandCurrency)}${formatPremiumAmount(originalAmount)}`
        : undefined,
    discountLabel:
      discountPercent !== undefined
        ? tr('discountOff', content, {
            percent: String(discountPercent),
            // 折 (zhe) value for ZH, e.g. 50% off → 5折 (String drops a trailing .0).
            zhe: String((100 - discountPercent) / 10),
          })
        : undefined,
    // ISO currency code + period, e.g. "SGD / trip" / "HKD / year"
    // (Figma node 21471:41978).
    period: `${currencyCode} / ${tr(isAnnual ? 'perYear' : 'perTrip', content)}`,
  };
};

// Summary-panel price, e.g. "SGD $24" + struck "SGD$40". No discount label or
// period — the discount goes in the highlight strip, per Figma 27410:57429.
export const formatSummaryPrice = (
  price: TiPremiumPrice,
  currencyCode: string,
  brandCurrency: string | undefined,
): { offered: string; original?: string } => ({
  offered: `${currencyCode} ${formatOfferedPrice(price, brandCurrency)}`,
  original:
    price.originalAmount !== undefined
      ? `${currencyCode}${symbolOf(price, brandCurrency)}${formatPremiumAmount(price.originalAmount)}`
      : undefined,
});

// Tracking `items[]` price fields (CMS-1795). `discount` is the amount
// deducted, not the percent.
export const resolveTrackingPrice = (
  price: TiPremiumPrice | undefined,
  currencyCode: string,
): JsonObject => {
  if (!price) {
    return {};
  }

  const fields: JsonObject = { price: price.amount, currency: currencyCode };
  if (price.originalAmount !== undefined) {
    // Round the float subtraction: single-trip premiums carry cents, so
    // 48 - 24.99 must not surface as 23.009999999999998.
    fields.discount = Number((price.originalAmount - price.amount).toFixed(2));
  }

  return fields;
};

// Localized traveller count, e.g. "2 Adults, 1 Child" or "Family (2 Adults,
// 2 Children)" when travelAsFamily is set (CMS-1864 Q3 default format).
export const formatTravellers = (
  trip: Pick<
    PlanTierSummaryTrip,
    'noOfAdults' | 'noOfChildren' | 'travelAsFamily'
  >,
  content: Content,
): string | undefined => {
  const parts: string[] = [];
  if (trip.noOfAdults === 1) {
    parts.push(tr('adultOne', content));
  } else if (trip.noOfAdults > 1) {
    parts.push(tr('adultsMany', content, { count: String(trip.noOfAdults) }));
  }
  if (trip.noOfChildren === 1) {
    parts.push(tr('childOne', content));
  } else if (trip.noOfChildren > 1) {
    parts.push(
      tr('childrenMany', content, { count: String(trip.noOfChildren) }),
    );
  }
  if (parts.length === 0) {
    return undefined;
  }
  const list = parts.join(tr('listSeparator', content));
  return trip.travelAsFamily
    ? tr('familyWrap', content, { travellers: list })
    : list;
};

export type SummaryRow = {
  id: string;
  icon: MaterialIconName;
  label: string;
  value: string;
};

// Trip-detail rows for the summary panel, built from the active search params.
// Rows without a resolvable value are omitted (never an empty label).
export const buildSummaryRows = (
  trip: PlanTierSummaryTrip,
  isAnnual: boolean,
  content: Content,
  lang: string,
): SummaryRow[] => {
  const rows: SummaryRow[] = [];

  rows.push({
    id: 'coverType',
    icon: 'new_releases',
    label: tr('coverType', content),
    value: tr(isAnnual ? 'coverTypeAnnual' : 'coverTypeSingle', content),
  });

  // Destination shows the resolved country/region labels joined with the
  // localized list separator (the label resolution itself is data, done upstream).
  const destinationValue =
    trip.destinationLabels.length > 0
      ? trip.destinationLabels.join(tr('listSeparator', content))
      : undefined;
  if (destinationValue) {
    rows.push({
      id: 'destination',
      icon: 'flightsmode',
      label: tr('destination', content),
      value: destinationValue,
    });
  }

  if (trip.startDate) {
    rows.push({
      id: 'startDate',
      icon: 'date_range',
      label: tr('startDate', content),
      value: formatData(trip.startDate, 'MMM dd, yyyy', lang),
    });
  }
  if (trip.endDate) {
    rows.push({
      id: 'endDate',
      icon: 'date_range',
      label: tr('endDate', content),
      value: formatData(trip.endDate, 'MMM dd, yyyy', lang),
    });
  }

  const travellers = formatTravellers(trip, content);
  if (travellers) {
    rows.push({
      id: 'travellers',
      icon: 'person',
      label: tr('travellers', content),
      value: travellers,
    });
  }

  return rows;
};

// Discount highlight strip copy, e.g. "Only with SingSaver you get 50% off".
// Undefined when there is no discount (the strip is then omitted).
export const formatDiscountHighlight = (
  discountPercent: number,
  brandName: string,
  content: Content,
): string | undefined =>
  discountPercent > 0
    ? tr('discountHighlight', content, {
        brand: brandName,
        percent: String(discountPercent),
      })
    : undefined;
